import { Phone, MessageCircle, MapPin, Instagram, FileText, Link2, QrCode } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { buildDirectionsUrl, trackDirectionsClick } from "@/lib/location-links";

interface QuickContactProps {
  /** Where this block is rendered — used for analytics. */
  location: string;
  /** Visual treatment. `dark` works inside the footer; `light` for in-page sections. */
  variant?: "light" | "dark";
  /** Optional eyebrow + headline + subline override. */
  eyebrow?: string;
  headline?: string;
  subline?: string;
  /** Hide the QR card on smaller layouts (footer compact, mobile stacked, etc). */
  showQR?: boolean;
}

/**
 * QuickContact — premium direct-action hub.
 *
 * Standardised CTA hierarchy:  Call  →  WhatsApp  →  Request a Booking  →  Directions  →  Instagram / Linktree.
 * Includes the Linktree QR card on desktop so visitors can hop to mobile in one scan.
 */
export function QuickContact({
  location,
  variant = "light",
  eyebrow = "Quick contact",
  headline = "The fastest way to reach Woodseer Autos.",
  subline = "Phone, WhatsApp or pop in. Scan the code to open every Woodseer link on your phone.",
  showQR = true,
}: QuickContactProps) {
  const biz = getBusinessConfig();
  const isDark = variant === "dark";

  const wrap = isDark
    ? "rounded-3xl border border-white/10 bg-white/[0.04] p-6 md:p-8 backdrop-blur-sm"
    : "rounded-3xl surface-premium p-6 md:p-8";
  const eyebrowCls = isDark ? "text-primary/80" : "text-primary";
  const headlineCls = isDark ? "text-white" : "text-foreground";
  const sublineCls = isDark ? "text-white/85" : "text-muted-foreground";
  const tileBase = isDark
    ? "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20"
    : "bg-card border-border hover:border-primary/40 hover:shadow-md";
  const labelMuted = isDark ? "text-white/80" : "text-muted-foreground";
  const valueText = isDark ? "text-white" : "text-foreground";
  const qrCardCls = isDark
    ? "border-white/10 bg-white/[0.03]"
    : "border-border bg-card";

  return (
    <div className={wrap}>
      <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
        <div className="min-w-0">
          <p className={`eyebrow ${eyebrowCls}`}>{eyebrow}</p>
          <h3 className={`mt-3 text-2xl md:text-3xl font-extrabold tracking-tight ${headlineCls}`}>{headline}</h3>
          <p className={`mt-3 text-sm md:text-[15px] leading-relaxed max-w-xl ${sublineCls}`}>{subline}</p>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {/* 1. CALL — primary */}
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { location: `quick_contact_${location}` })}
              className="group inline-flex items-center gap-2.5 rounded-xl bg-primary text-primary-foreground px-4 py-3 font-bold text-sm shadow-md shadow-primary/20 hover:bg-primary/90 transition-all col-span-2 sm:col-span-3"
              aria-label={`Call Woodseer Autos on ${biz.phone}`}
            >
              <Phone className="h-4 w-4 shrink-0" />
              <span className="truncate">Call {biz.phone}</span>
            </a>

            {/* 2. WHATSAPP */}
            <a
              href={biz.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: `quick_contact_${location}` })}
              className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold transition-all ${
                isDark
                  ? "border-[#25D366]/30 bg-[#25D366]/5 text-[#25D366] hover:bg-[#25D366]/10"
                  : "border-[#25D366]/30 bg-[#25D366]/5 text-[#1ea952] hover:bg-[#25D366]/10"
              }`}
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>

            {/* 3. REQUEST A BOOKING */}
            <Link
              to="/book"
              onClick={() => trackEvent("service_cta_click", { location: `quick_contact_${location}` })}
              className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold transition-all ${tileBase} ${valueText}`}
            >
              <FileText className="h-4 w-4" />
              Request booking
            </Link>

            {/* 4. DIRECTIONS */}
            <a
              href={buildDirectionsUrl("entrance")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackDirectionsClick({ source: `quick_contact_${location}`, kind: "entrance", action: "directions_tap" })}
              className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold transition-all ${tileBase} ${valueText}`}
            >
              <MapPin className="h-4 w-4" />
              Directions
            </a>

            {/* 5. INSTAGRAM */}
            <a
              href={biz.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("instagram_click", { location: `quick_contact_${location}` })}
              className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold transition-all ${tileBase} ${valueText}`}
            >
              <Instagram className="h-4 w-4" />
              Instagram
            </a>

            {/* 6. LINKTREE */}
            <a
              href={biz.linktreeUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("linktree_click", { location: `quick_contact_${location}` })}
              className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold transition-all ${tileBase} ${valueText}`}
            >
              <Link2 className="h-4 w-4" />
              All links
            </a>
          </div>

          <p className={`mt-4 text-[11px] ${labelMuted}`}>
            Mon–Sat 9–6 · Yard entrance via 14 Copenhagen Place, E14 7DF
          </p>
        </div>

        {/* QR — desktop scan-to-mobile card */}
        {showQR && (
          <a
            href={biz.linktreeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("linktree_click", { location: `quick_contact_${location}_qr` })}
            className={`hidden md:flex flex-col items-center gap-3 rounded-2xl border p-5 transition-all hover:scale-[1.02] hover:shadow-lg ${qrCardCls}`}
            aria-label="Open Woodseer Autos Linktree — or scan the QR with your phone"
          >
            <div className="rounded-xl bg-white p-2.5 shadow-sm">
              <img
                src={biz.qrImagePath}
                alt="Scan to open all Woodseer Autos links"
                width={140}
                height={140}
                className="h-[140px] w-[140px]"
              />
            </div>
            <div className="text-center max-w-[160px]">
              <p className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${eyebrowCls}`}>
                <QrCode className="h-3 w-3" />
                Scan
              </p>
              <p className={`mt-1.5 text-[12px] font-semibold leading-snug ${valueText}`}>
                Open every Woodseer link on your phone
              </p>
            </div>
          </a>
        )}
      </div>
    </div>
  );
}
